import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, safeDocumentOperation } from '../utils/api';

// Static Content Component
const StaticContentSection = ({ page, setSEO = false, hideSEOBlock = false }) => {
  const [content, setContent] = useState([]);
  const [seoData, setSeoData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStaticContent = async () => {
      try {
        setLoading(true);
        
        // Fetch static content for visible content
        const contentResponse = await axios.get(`${API}/static-content/${page}`);
        if (contentResponse.data.success) {
          setContent(contentResponse.data.data);
        }
        
        // Fetch SEO data if setSEO is true
        if (setSEO) {
          try {
            const seoResponse = await axios.get(`${API}/seo/${page}`);
            if (seoResponse.data.success && seoResponse.data.data.length > 0) {
              const seoInfo = seoResponse.data.data[0];
              setSeoData(seoInfo);
              
              // Apply meta tags
              safeDocumentOperation(() => {
                if (seoInfo.meta_title) {
                  document.title = seoInfo.meta_title;
                }
                
                if (seoInfo.meta_description) {
                  let metaDescription = document.querySelector('meta[name="description"]');
                  if (!metaDescription) {
                    metaDescription = document.createElement('meta');
                    metaDescription.name = 'description';
                    document.head.appendChild(metaDescription);
                  }
                  metaDescription.content = seoInfo.meta_description;
                }
              });
            }
          } catch (seoError) {
            console.log('SEO data not available for page:', page);
          }
        }
      } catch (error) {
        console.error('Error fetching static content:', error);
      } finally {
        setLoading(false);
      }
    };

    if (page) {
      fetchStaticContent();
    }
  }, [page, setSEO]);

  if (loading) {
    return null;
  }

  const hasSEOContent = seoData && (seoData.h1 || seoData.p);
  const visibleContent = content.filter(item => item.active && item.title && item.content);

  return (
    <div className="space-y-8">
      {/* SEO Content (h1 and p from SEO sheet) - hidden if displayed elsewhere */}
      {hasSEOContent && !hideSEOBlock && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
          {seoData.h1 && (
            <h1 className="text-3xl font-bold text-gray-900 mb-6">{seoData.h1}</h1>
          )}
          {seoData.p && (
            <div className="prose max-w-none text-gray-700 leading-relaxed">
              {seoData.p.split('\n').map((paragraph, index) => (
                paragraph.trim() && (
                  <p key={index} className="mb-4">
                    {paragraph.trim()}
                  </p>
                )
              ))}
            </div>
          )}
        </div>
      )}

      {/* Static Content (always show if available) */}
      {visibleContent.length > 0 && visibleContent.map((item) => (
        <div key={item.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">{item.title}</h2>
          <div className="prose max-w-none text-gray-700 leading-relaxed">
            {item.content.split('\n').map((paragraph, index) => (
              paragraph.trim() && (
                <p key={index} className="mb-4">
                  {paragraph.trim()}
                </p>
              )
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default StaticContentSection;
